import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { forwardRef } from 'react';
import { type ClassName, twx } from '@/utils/tailwind';

export const chipButtonTones = [
  /** Default — neutral white pill that becomes the secondary accent when
   * selected. Used for radius/category pills in FiltersPopover. */
  'neutral',
  /** Day-pager arrow buttons in Curate / Travel — same neutral default
   * with a secondary-tinted hover/disabled. */
  'paging',
] as const;
export type ChipButtonTone = (typeof chipButtonTones)[number];

export const chipButtonSizes = ['xs', 'sm'] as const;
export type ChipButtonSize = (typeof chipButtonSizes)[number];

interface SizeSpec {
  padding: string;
  text: string;
  iconPx: number;
  height: string;
}

const sizeSpecs: Record<ChipButtonSize, SizeSpec> = {
  xs: { padding: 'px-3 py-1', text: 'text-xs', iconPx: 11, height: 'h-7' },
  sm: { padding: 'px-3', text: 'text-xs', iconPx: 11, height: 'h-9' },
};

const toneClasses = {
  neutral: {
    base: 'bg-white text-content-text border-content-border hover:border-secondary-main',
    selected: 'bg-secondary-emphasized text-white border-secondary-emphasized',
  },
  paging: {
    base: 'bg-white text-content-subtle border-content-border shadow-sm hover:bg-content-focal hover:border-secondary-main hover:text-secondary-emphasized',
    selected: 'bg-secondary-emphasized text-white border-secondary-emphasized',
  },
} as const satisfies Record<ChipButtonTone, { base: string; selected: string }>;

type NativeButtonProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'className' | 'children'
>;

export interface ChipButtonProps extends NativeButtonProps {
  tone?: ChipButtonTone;
  size?: ChipButtonSize;
  /** Selected state — fills with the tone's selected color. */
  selected?: boolean;
  /** Optional leading icon. */
  icon?: IconDefinition;
  className?: ClassName;
  children?: React.ReactNode;
}

/**
 * Rounded-full pill button with a selected/unselected toggle. Covers:
 *   - Radius pills + category pills in the filters popover
 *   - The Prev/Next day pager buttons in Curate + Travel
 *   - Future single-choice chip groups (no need to invent a new style)
 */
export const ChipButton = forwardRef<HTMLButtonElement, ChipButtonProps>(
  (
    { tone = 'neutral', size = 'xs', selected = false, icon, className, type, children, ...rest },
    ref,
  ) => {
    const spec = sizeSpecs[size];
    const tones = toneClasses[tone];
    return (
      <button
        ref={ref}
        type={type ?? 'button'}
        className={twx(
          'inline-flex items-center gap-1.5 rounded-full font-medium border transition-[colors,transform] duration-150 disabled:opacity-30 disabled:cursor-not-allowed',
          'motion-safe:active:scale-[0.94]',
          spec.padding,
          spec.text,
          spec.height,
          selected ? tones.selected : tones.base,
          className,
        )}
        {...rest}
      >
        {icon && (
          <FontAwesomeIcon
            icon={icon}
            style={{ width: spec.iconPx, height: spec.iconPx }}
            className="shrink-0"
          />
        )}
        {children}
      </button>
    );
  },
);
ChipButton.displayName = 'ChipButton';
